/* eslint no-console: ["error", { allow: ["warn", "error"] }] */

function lpOrchestratorInit() {
  if (!window?.LiveChat) return;

  const defaultOrcValues = {
    engId: '',
    isLoopActive: false,
    isUserAwaitingChat: false,
    intentLoopTimeoutDefault: 15,
    intentLoopTimeoutCounter: 15
  };

  window.LiveChat.lpChatOrcData = {
    ...defaultOrcValues,
    engKey: '',
    intentLoopFinishedEventName: 'global_chat_intent_loop_complete',
    errorMessageEventName: 'chat_button_error'
  };

  window.LiveChat.lpBeautyChatOrcData = {
    ...defaultOrcValues,
    engKey: 'livechat_beauty_drawer',
    intentLoopFinishedEventName: 'livechat_beauty_drawer_intent_loop_complete',
    errorMessageEventName: 'beauty_chat_drawer_error'
  };

  window.LiveChat.lpSupportChatOrcData = {
    ...defaultOrcValues,
    engKey: 'livechat_support_drawer',
    intentLoopFinishedEventName: 'livechat_support_drawer_intent_loop_complete',
    errorMessageEventName: 'support_chat_drawer_error'
  };

  window.LiveChat.lpPageChatOrcData = {
    ...defaultOrcValues,
    engKey: 'livechat_default_page',
    intentLoopFinishedEventName: 'livechat_default_page_intent_loop_complete',
    errorMessageEventName: 'page_engagement_error'
  };

  function initEngagement(engKey: EngagementKey, engId: string): boolean {
    if (!window.LiveChat) return false;

    switch (engKey) {
      case window.LiveChat.lpBeautyChatOrcData?.engKey:
        window.LiveChat.lpBeautyChatOrcData!.engId = engId;
        return true;
      case window.LiveChat?.lpSupportChatOrcData?.engKey:
        window.LiveChat.lpSupportChatOrcData!.engId = engId;
        return true;
      case window.LiveChat.lpPageChatOrcData?.engKey:
        window.LiveChat.lpPageChatOrcData!.engId = engId;
        return true;
      case window.LiveChat.lpChatOrcData?.engKey:
        window.LiveChat.lpChatOrcData!.engId = engId;
        return true;

      default:
        throw new Error(
          `lpOrchestrator.initEngagement(): The div id used in this script is not recognized: ${engKey}. The chat module for this site is misconfigured.`
        );
    }
  }

  function verifyEngagementIsReady(engKey: EngagementKey): boolean {
    const lpEngagementDiv = document.getElementById(engKey);

    if (
      !lpEngagementDiv ||
      lpEngagementDiv.children.length === 0 ||
      lpEngagementDiv.children[0].children.length === 0
    ) {
      return false;
    }

    const engId = lpEngagementDiv.children[0].children[0].getAttribute('data-lp-engagement-id');

    return engId ? initEngagement(engKey, engId) : false;
  }

  function verifyLivePersonIsReady(): boolean {
    return !!window.lpTag?.taglets?.rendererStub;
  }

  function verifyServiceIsReady(engKey: EngagementKey): boolean {
    if (!verifyLivePersonIsReady()) return false;
    return verifyEngagementIsReady(engKey);
  }

  function performEngagementClick(engId: string): boolean {
    return window.lpTag?.taglets?.rendererStub && engId
      ? window.lpTag.taglets.rendererStub.click(engId)
      : false;
  }

  function handleIntervalFail(
    chatOrcData: ChatOrcData,
    lpErrorMessageEvent: Event,
    intentLoop?: number
  ): void {
    if (!chatOrcData.intentLoopTimeoutCounter) return;
    chatOrcData.intentLoopTimeoutCounter--;
    if (chatOrcData.intentLoopTimeoutCounter === 0) {
      chatOrcData.isLoopActive = false;
      clearInterval(intentLoop);
      document.dispatchEvent(lpErrorMessageEvent);
    }
  }

  function getChatOrcData(engKey: EngagementKey): ChatOrcData | undefined {
    switch (engKey) {
      case window.LiveChat!.lpBeautyChatOrcData?.engKey:
        return window.LiveChat!.lpBeautyChatOrcData!;
      case window.LiveChat!.lpSupportChatOrcData?.engKey:
        return window.LiveChat!.lpSupportChatOrcData!;
      case window.LiveChat!.lpPageChatOrcData?.engKey:
        return window.LiveChat!.lpPageChatOrcData!;
      case window.LiveChat!.lpChatOrcData?.engKey:
        return window.LiveChat!.lpChatOrcData!;
      default:
        return;
    }
  }

  async function startIntentLoop(engKey: EngagementKey): Promise<void> {
    if (!window?.LiveChat) return;

    try {
      const chatOrcData = getChatOrcData(engKey);

      if (!chatOrcData) {
        throw new Error(
          `lpOrchestrator: The div id used in the function startIntentLoop() is not recognized: ${engKey}. The chat module for this site is misconfigured.`
        );
      }

      if (Object.keys(chatOrcData).length === 0) {
        throw new Error('lpOrchestrator: The lp__ChatOrcData object is empty. Something is wrong.');
      }

      if (!document.getElementById(engKey)) {
        throw new Error(
          `lpOrchestrator.initEngagement(): The LivePerson HTMLEngagement div was not found: ${engKey} - The LiveChat system for this site is misconfigured.`
        );
      }

      chatOrcData.isLoopActive = true;
      chatOrcData.isUserAwaitingChat = true;

      const eventName: IntentLoopEventName = chatOrcData.intentLoopFinishedEventName;
      const errorEventName: ErrorMessageEventName = chatOrcData.errorMessageEventName;
      const lpIntentLoopCompleteEvent = new Event(eventName, { bubbles: true });
      const lpErrorMessageEvent = new Event(errorEventName, { bubbles: true });

      // Listen for the loop completion event
      document.addEventListener(eventName, function () {
        resetAfterIntentLoop(chatOrcData);
      });

      // Listen for the error event
      document.addEventListener(errorEventName, function () {
        resetAfterIntentLoop(chatOrcData);
        console.warn(
          `lpOrchestrator: The attempt to initiate a LivePerson chat session was unsuccessful.`
        );
      });

      const success = handleIntent({
        key: engKey,
        chatOrcData,
        intentCompleteEvent: lpIntentLoopCompleteEvent,
        errorEvent: lpErrorMessageEvent
      });

      if (success) return;

      chatOrcData.intentLoop = setInterval(() => {
        handleIntent({
          key: engKey,
          chatOrcData,
          intentCompleteEvent: lpIntentLoopCompleteEvent,
          errorEvent: lpErrorMessageEvent
        });
      }, 1000);
    } catch (e) {
      document.dispatchEvent(new Event('livechat_ui_error', { bubbles: true }));
      console.error(e);
    }
  }

  type HandleIntentArgs = {
    key: EngagementKey;
    chatOrcData: ChatOrcData;
    intentCompleteEvent: Event;
    errorEvent: Event;
  };

  function handleIntent({ key, chatOrcData, intentCompleteEvent, errorEvent }: HandleIntentArgs) {
    if (!verifyServiceIsReady(key)) {
      handleIntervalFail(chatOrcData, errorEvent, chatOrcData.intentLoop);
      return;
    }

    if (performEngagementClick(chatOrcData.engId)) {
      chatOrcData.isLoopActive = false;
      clearInterval(chatOrcData.intentLoop);
      document.dispatchEvent(intentCompleteEvent);
      return true;
    } else {
      handleIntervalFail(chatOrcData, errorEvent, chatOrcData.intentLoop);
      return false;
    }
  }

  async function endIntentLoop(engKey: EngagementKey): Promise<void> {
    if (!window?.LiveChat) return;

    try {
      const chatOrcData = getChatOrcData(engKey);

      if (!chatOrcData) {
        throw new Error(
          `lpOrchestrator: The div id used in the function endIntentLoop() is not recognized: ${engKey}. The chat module for this site is misconfigured.`
        );
      }

      // Ensure chatOrcData exists and is valid
      if (Object.keys(chatOrcData).length === 0) {
        throw new Error('lpOrchestrator: The lp__ChatOrcData object is empty. Something is wrong.');
      }

      if (!chatOrcData.isLoopActive) return;

      // Clear the active intent loop, if any
      const eventName = chatOrcData.intentLoopFinishedEventName;
      const lpIntentLoopCompleteEvent = new Event(eventName, { bubbles: true });
      document.addEventListener(eventName, function () {
        resetAfterIntentLoop(chatOrcData);
        chatOrcData.isLoopActive = false;
      });
      document.dispatchEvent(lpIntentLoopCompleteEvent);
    } catch (e) {
      console.error(`Error in endIntentLoop for engKey: ${engKey}`, e);
    }
  }

  function resetAfterIntentLoop(chatOrcData: ChatOrcData) {
    clearInterval(chatOrcData.intentLoop);
    chatOrcData.intentLoop = undefined;
    chatOrcData.intentLoopTimeoutCounter = chatOrcData.intentLoopTimeoutDefault;
    chatOrcData.isUserAwaitingChat = false;
  }

  // Fires when the user clicks the Beauty Chat button in the drawer
  window.LiveChat.onBeautyChatButtonClick = function (): void {
    if (
      window.LiveChat?.lpBeautyChatOrcData?.isLoopActive ||
      !window.LiveChat?.lpBeautyChatOrcData?.engKey
    ) {
      return;
    }
    startIntentLoop(window.LiveChat.lpBeautyChatOrcData.engKey);
  };

  // Fires when the user clicks the Support Chat button in the drawer
  window.LiveChat.onSupportChatButtonClick = function (): void {
    if (
      window.LiveChat?.lpSupportChatOrcData?.isLoopActive ||
      !window.LiveChat?.lpSupportChatOrcData?.engKey
    ) {
      return;
    }
    startIntentLoop(window.LiveChat.lpSupportChatOrcData.engKey);
  };

  window.LiveChat.onPageEngagementClick = function (): void {
    if (
      window.LiveChat?.lpPageChatOrcData?.isLoopActive ||
      !window.LiveChat?.lpPageChatOrcData?.engKey
    ) {
      return;
    }
    startIntentLoop(window.LiveChat?.lpPageChatOrcData.engKey);
  };

  // Fires when the user clicks the Chat button
  window.LiveChat.onChatButtonClick = function (engKey: EngagementKey): void {
    if (window.LiveChat?.lpChatOrcData?.isLoopActive || !window.LiveChat?.lpChatOrcData) {
      return;
    }
    window.LiveChat.lpChatOrcData.engKey = engKey;
    startIntentLoop(window.LiveChat.lpChatOrcData.engKey);
  };

  // Fires when the user clicks the close chat drawer button
  window.LiveChat.onCloseButtonClick = function (engKey?: EngagementKey): void {
    if (window.LiveChat?.lpBeautyChatOrcData?.isLoopActive) {
      endIntentLoop(window.LiveChat?.lpBeautyChatOrcData.engKey);
    } else if (window.LiveChat?.lpSupportChatOrcData?.isLoopActive) {
      endIntentLoop(window.LiveChat?.lpSupportChatOrcData.engKey);
    } else if (window.LiveChat?.lpChatOrcData?.isLoopActive && engKey) {
      window.LiveChat.lpChatOrcData.engKey = engKey;
      endIntentLoop(window.LiveChat.lpChatOrcData.engKey);
    }
  };
}

lpOrchestratorInit();
